import { Alert, AlertDescription, AlertIcon, Button, chakra, Flex, Text } from "@chakra-ui/react";
import React from "react";

interface Props {
  /** false disables Save (e.g. "Other" selected without text) */
  canSave?: boolean;
  children: React.ReactNode;
  error: string | null;
  isSaving: boolean;
  label: string;
  /** The submit button's text; "Save" by default */
  saveLabel?: string;
  onCancel: () => void;
  onSave: () => void;
}

/** Shared frame of every inline editor: label, inputs, error with Retry, Cancel / Save. */
export function EditorShell(props: Props) {
  const canSave = props.canSave ?? true;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (canSave && !props.isSaving) {
      props.onSave();
    }
  };

  return (
    <chakra.form
      bg="white"
      borderColor="gray.200"
      borderRadius="md"
      borderWidth="1px"
      display="flex"
      flexDirection="column"
      gap={3}
      // Editors validate through `canSave`; native validation would block Save on reused
      // components' `required` inputs (e.g. every POC item checkbox of a required POC question)
      noValidate={true}
      p={3}
      onSubmit={handleSubmit}
    >
      <Text fontSize="sm" fontWeight="semibold">
        {props.label}
      </Text>

      {/* A disabled fieldset natively disables every input inside while saving */}
      <chakra.fieldset border="none" disabled={props.isSaving} m={0} minW={0} p={0}>
        {props.children}
      </chakra.fieldset>

      {props.error !== null && (
        <Alert alignItems="center" borderRadius="md" gap={2} py={2} status="error">
          <AlertIcon />
          <AlertDescription flex={1} fontSize="sm">
            {props.error}
          </AlertDescription>
          <Button
            colorScheme="red"
            isDisabled={!canSave}
            isLoading={props.isSaving}
            size="sm"
            variant="outline"
            onClick={props.onSave}
          >
            Retry
          </Button>
        </Alert>
      )}

      <Flex gap={2} justifyContent="flex-end">
        <Button isDisabled={props.isSaving} size="sm" variant="ghost" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button
          colorScheme="blue"
          isDisabled={!canSave}
          isLoading={props.isSaving}
          size="sm"
          type="submit"
        >
          {props.saveLabel ?? "Save"}
        </Button>
      </Flex>
    </chakra.form>
  );
}
